/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import * as strings from '@touchplan/strings';
import { from, Observable, of, Subject } from 'rxjs';
import {
	concatMap,
	delay,
	filter,
	map,
	observeOn,
	switchMap,
	take,
	takeUntil,
	withLatestFrom,
} from 'rxjs/operators';
import { cssVar } from '@touchplan/css-var';
import { Organization } from 'src/app/app-data/models/Organization';
import { OrganizationService, RequestState } from '../../services/organization.service';
import { AppSecretDialog } from '../app-secret-dialog/app-secret-dialog.component';
import { asyncScheduler } from 'rxjs';

@Component({
	selector: 'organization-app-editor',
	templateUrl: './organization-app-editor.component.html',
	styleUrls: ['./organization-app-editor.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationAppEditorComponent implements OnInit, OnDestroy {
	public strings = strings;
	private shutdownSubject$ = new Subject<void>();
	public applications$: Observable<Organization[]>;
	public requestState$: Observable<string>;

	/** Wraps the application being defined into an async scheduler to ensure child lifecycle hooks fire */
	public showEdit$: Observable<boolean>;

	constructor(public organizationService: OrganizationService, public dialog: MatDialog) {}

	public static openOrganizationApiSettings(dialog: MatDialog): void {
		dialog.open(OrganizationAppEditorComponent, this.getDialogConfig());
	}

	private static getDialogConfig(): MatDialogConfig {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = true;
		dialogConfig.width = cssVar.apiSettingsDialogWidth;
		dialogConfig.autoFocus = true;
		return dialogConfig;
	}

	public ngOnInit(): void {
		this.requestState$ = this.organizationService.requestState$.pipe(
			switchMap((state) => {
				switch (state) {
					case RequestState.Error:
						return from(['Failure', null]).pipe(concatMap((item) => of(item).pipe(delay(1000))));
					case RequestState.Success:
						return from(['Success', null]).pipe(concatMap((item) => of(item).pipe(delay(500))));
					case RequestState.WaitingForServer:
						return of('InProgress');
					default:
						return of(null);
				}
			})
		);

		this.organizationService.appSecretGenerated$
			.pipe(
				filter((secretKey) => !!secretKey),
				delay(1000), // Delay so we show the success request state
				withLatestFrom(this.organizationService.application$, this.organizationService.applicationUser$),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(([secretKey, appOrg, appUser]) => {
				// generate secret button click in OrganizationAppInfo → organizationService.generateSecret() called →
				// emit new value of appSecretGenerated$  → opening AppSecretDialog dialog
				if (appOrg && appUser) {
					const data = {
						title: strings.ORG_CRED_DIALOG_TITLE,
						content: strings.ORG_CRED_DIALOG_LINES(appOrg.view.name),
						clientId: appUser.$id,
						password: secretKey,
						noButtonText: strings.UICONTROL_TEXT_CLOSE.toUpperCase(),
					};
					this.dialog.open(AppSecretDialog, { data });
				}
			});
		this.applications$ = this.organizationService.applicationList$.pipe(
			map((list) => [...list?.values()].filter((item) => item.view.deletedAt !== null)),
			takeUntil(this.shutdownSubject$)
		);

		this.showEdit$ = this.organizationService.applicationChange$.pipe(
			map((a) => !!a),
			//forces this observable to always emit async. Roughly the same as setTimeout of 0 from the olden days
			observeOn(asyncScheduler)
		);
	}

	public ngOnDestroy(): void {
		// Do this so that unsaved changes when the user clicks the "X" at the top-right of
		// the dialog are discarded, as if the the Cancel button was implicitly clicked first.
		this.organizationService.discard();

		// Remove any added applications that the user didn't end up saving:
		this.organizationService.applicationList$
			.pipe(
				map((list) => [...list?.values()].filter((elem) => elem.localDataNotYetPersisted)),
				take(1)
			)
			.subscribe((orgs) => {
				this.organizationService.removeUnsavedApplication(orgs);
			});

		this.shutdownSubject$.next(undefined);
		this.shutdownSubject$.complete();
	}

	public startAdd(): void {
		this.organizationService.addApplication();
	}

	public startEdit(organization: Organization): void {
		this.organizationService.editApplication(organization);
	}
}
